import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, Typography } from "@mui/material";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from "react-router-dom";
import { Product } from './MobileMagazines';
import { formatMagazineTitle, getMagazineDisplayName, FormattedMagazineTitle } from '../../utils/magazineTitleUtils';

interface MagazinesCarouselProps {
    magazinesList: any[];
    totalRecords: any;
    magazineLang?: any;
    magazineLanguageId?: any;
    itemType?: string;
    /** Product.MonthlyMagazine | Product.Kalyan | Product.KalyanKalpataru */
    magazineProductType?: number;
    /** Kalyan/Kalpataru category — View All → mMagazineListPage */
    magazineCategoryId?: string;
    magazineCategoryName?: string;
    magazineCategoryLanguage?: any;
    t: (key: string) => string;
    /** When true, parent already provides the white card surface */
    embedded?: boolean;
}

const THUMB_COLOR = '#f97316';
const THUMB_SIZE = 28; // same pill length as TOC vertical scrollbar
const TRACK_THICKNESS = 6;
const PILL_THICKNESS = TRACK_THICKNESS * 2;
const HIT_THICKNESS = Math.max(PILL_THICKNESS, 28); // finger-friendly track

const MagazinesCarousel: React.FC<MagazinesCarouselProps> = ({
    magazinesList,
    totalRecords,
    magazineLang,
    magazineLanguageId,
    itemType,
    magazineProductType = Product.MonthlyMagazine,
    magazineCategoryId,
    magazineCategoryName,
    magazineCategoryLanguage,
    t,
    embedded = false,
}) => {
    const navigate = useNavigate();
    const scrollRef = useRef<HTMLDivElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<{ active: boolean; offsetX: number }>({ active: false, offsetX: 0 });
    const itemTapRef = useRef<{ x: number; y: number; moved: boolean }>({ x: 0, y: 0, moved: false });
    const [thumbLeft, setThumbLeft] = useState(0);
    const [showThumb, setShowThumb] = useState(false);

    const getMetrics = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return null;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const maxScroll = Math.max(0, scrollWidth - clientWidth);
        const maxThumbLeft = Math.max(0, clientWidth - THUMB_SIZE);
        return { el, scrollLeft, scrollWidth, clientWidth, maxScroll, maxThumbLeft };
    }, []);

    const scrollToThumbLeft = useCallback((nextThumbLeft: number) => {
        const m = getMetrics();
        if (!m || m.maxScroll <= 0 || m.maxThumbLeft <= 0) return;
        const clamped = Math.min(m.maxThumbLeft, Math.max(0, nextThumbLeft));
        m.el.scrollLeft = (clamped / m.maxThumbLeft) * m.maxScroll;
        setThumbLeft(clamped);
    }, [getMetrics]);

    // Sync custom orange horizontal pill with scroll position
    const syncThumb = useCallback(() => {
        if (dragRef.current.active) return;
        const m = getMetrics();
        if (!m) return;
        const canScroll = m.scrollWidth > m.clientWidth + 1;
        setShowThumb(canScroll);
        if (!canScroll) {
            setThumbLeft(0);
            return;
        }
        setThumbLeft(m.maxScroll > 0 ? (m.scrollLeft / m.maxScroll) * m.maxThumbLeft : 0);
    }, [getMetrics]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        syncThumb();
        el.addEventListener('scroll', syncThumb, { passive: true });
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncThumb) : null;
        ro?.observe(el);
        return () => {
            el.removeEventListener('scroll', syncThumb);
            ro?.disconnect();
        };
    }, [syncThumb, magazinesList]);

    const onThumbPointerDown = (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const track = trackRef.current;
        if (!track) return;
        const trackRect = track.getBoundingClientRect();
        dragRef.current = {
            active: true,
            offsetX: e.clientX - trackRect.left - thumbLeft,
        };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    };

    const onThumbPointerMove = (e: React.PointerEvent) => {
        if (!dragRef.current.active) return;
        e.preventDefault();
        const track = trackRef.current;
        if (!track) return;
        const trackRect = track.getBoundingClientRect();
        scrollToThumbLeft(e.clientX - trackRect.left - dragRef.current.offsetX);
    };

    const onThumbPointerUp = (e: React.PointerEvent) => {
        if (!dragRef.current.active) return;
        dragRef.current.active = false;
        try {
            (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
            /* already released */
        }
        syncThumb();
    };

    const onTrackPointerDown = (e: React.PointerEvent) => {
        if ((e.target as HTMLElement).dataset.thumb === '1') return;
        const track = trackRef.current;
        if (!track) return;
        const trackRect = track.getBoundingClientRect();
        scrollToThumbLeft(e.clientX - trackRect.left - THUMB_SIZE / 2);
    };

    const isSimpleLangLabel = (rawLang: any): boolean => {
        const lang = String(rawLang || '').toLowerCase();
        return lang === 'english' || lang === 'en' || lang === 'hindi' || lang === 'hi';
    };

    // Normalize language values (english/en → english, hindi/hi → hindi)
    const getLangLabel = (rawLang: any): string => {
        if (!rawLang && rawLang !== 0) return '';
        const lang = String(rawLang).toLowerCase();
        if (lang === 'english' || lang === 'en' || lang.includes('english') || lang.includes('angrezi')) return 'english';
        if (lang === 'hindi' || lang === 'hi' || lang.includes('hindi') || lang.includes('hin') || lang.includes('हिं') || lang.includes('हिन')) return 'hindi';
        return '';
    };

    // Resolve english/hindi label + API language id for View All navigation
    const resolveViewAllLanguage = () => {
        const logdetails: string[] = [];

        let langLabel = getLangLabel(magazineLang);

        if (!langLabel) {
            for (const magazine of magazinesList) {
                const inferred = getLangLabel(
                    magazine?.monthlyMagazineLanguage ||
                    magazine?.magazineLanguage ||
                    magazine?.magazineLangauge
                );
                if (inferred) {
                    langLabel = inferred;
                    break;
                }
            }
        }

        const languageId =
            magazineLanguageId ||
            (!isSimpleLangLabel(magazineLang) ? magazineLang : '') ||
            magazinesList?.[0]?.monthlyMagazineLanguageId ||
            magazinesList?.find((m) => m?.monthlyMagazineLanguageId)?.monthlyMagazineLanguageId ||
            (langLabel === 'english' || langLabel === 'hindi' ? langLabel : '');

        if (!langLabel && languageId) {
            langLabel = getLangLabel(languageId);
        }
        if (!langLabel) {
            langLabel = 'hindi';
        }

        logdetails.push(`resolved lang=${langLabel}`);
        logdetails.push(`resolved languageId=${languageId}`);

        return { langLabel, languageId, logdetails };
    };

    // View All — Geet Govind language list, or Kalyan / Kalpataru issue list (mMagazineListPage)
    const handleViewAllClick = () => {
        const logdetails: string[] = [];
        logdetails.push(`view_all_click totalRecords=${totalRecords}`);
        logdetails.push(`productType=${magazineProductType}`);
        logdetails.push(`categoryId=${magazineCategoryId || ''}`);

        if (magazineProductType === Product.Kalyan) {
            if (magazineCategoryId) {
                const path = `/magazines/kalyan/category/${magazineCategoryId}`;
                logdetails.push(`navigate=${path}`);
                navigate(path, {
                    state: {
                        kalyansId: magazineCategoryId,
                        categoryId: magazineCategoryId,
                        kalyansLanguage: magazineCategoryLanguage,
                        kalyanName: magazineCategoryName,
                        isKalpataruBook: false,
                        productId: Product.Kalyan,
                    },
                });
            } else {
                // No category context (deep-link) — fall back to category hub
                logdetails.push('navigate=/magazines/kalyan fallback_no_category');
                navigate('/magazines/kalyan', {
                    state: { productId: Product.Kalyan },
                });
            }
            console.log('[MagazinesCarousel]', logdetails.join(' | '));
            return;
        }

        if (magazineProductType === Product.KalyanKalpataru) {
            if (magazineCategoryId) {
                const path = `/magazines/kalyanakalpataru/category/${magazineCategoryId}`;
                logdetails.push(`navigate=${path}`);
                navigate(path, {
                    state: {
                        kalyansId: magazineCategoryId,
                        categoryId: magazineCategoryId,
                        kalyansLanguage: magazineCategoryLanguage,
                        kalyanName: magazineCategoryName,
                        kalpataruName: magazineCategoryName,
                        isKalpataruBook: true,
                        productId: Product.KalyanKalpataru,
                    },
                });
            } else {
                logdetails.push('navigate=/magazines/kalyanakalpataru fallback_no_category');
                navigate('/magazines/kalyanakalpataru', {
                    state: { productId: Product.KalyanKalpataru },
                });
            }
            console.log('[MagazinesCarousel]', logdetails.join(' | '));
            return;
        }

        const { langLabel, languageId, logdetails: resolveLogs } = resolveViewAllLanguage();
        logdetails.push(...resolveLogs);
        logdetails.push(`navigate=/magazines/geetgovind with lang=${langLabel}`);

        navigate(`/magazines/geetgovind`, {
            state: {
                type: 'geetGovind',
                geetGovindLang: langLabel,
                magazineLang: languageId,
                magazineLanguage: languageId,
                magazineLangauge: languageId,
            }
        });

        console.log('[MagazinesCarousel]', logdetails.join(' | '));
    };

    // Issue click — Geet Govind remounts hub; Kalyan / Kalpataru open magazine home by slug
    const handleMagazineClick = (magazine: any) => {
        const logdetails: string[] = [];
        const magazineId = magazine?.id;
        const slug = magazine?.slug || magazineId;

        logdetails.push(`carousel_click magazineId=${magazineId}`);
        logdetails.push(`slug=${slug}`);
        logdetails.push(`productType=${magazineProductType}`);
        logdetails.push(`categoryId=${magazineCategoryId || ''}`);

        if (magazineProductType === Product.Kalyan) {
            logdetails.push(`navigate=/magazines/kalyan/${slug}`);
            navigate(`/magazines/kalyan/${slug}`, {
                state: {
                    kalyansId: magazineId,
                    magzineId: magazineId,
                    bookName: getMagazineDisplayName(magazine),
                    slug,
                    isKalpataruBook: false,
                    type: 'kalyan',
                    categoryId: magazineCategoryId,
                    kalyanName: magazineCategoryName,
                    kalyansLanguage: magazineCategoryLanguage,
                },
            });
            console.log('[MagazinesCarousel]', logdetails.join(' | '));
            return;
        }

        if (magazineProductType === Product.KalyanKalpataru) {
            logdetails.push(`navigate=/magazines/kalyanakalpataru/${slug}`);
            navigate(`/magazines/kalyanakalpataru/${slug}`, {
                state: {
                    kalpataruId: magazineId,
                    kalyansId: magazineId,
                    magzineId: magazineId,
                    bookName: getMagazineDisplayName(magazine),
                    slug,
                    isKalpataruBook: true,
                    type: 'kalyanakalpataru',
                    categoryId: magazineCategoryId,
                    kalyanName: magazineCategoryName,
                    kalpataruName: magazineCategoryName,
                    kalyansLanguage: magazineCategoryLanguage,
                },
            });
            console.log('[MagazinesCarousel]', logdetails.join(' | '));
            return;
        }

        const rawLang =
            magazine?.magazineLanguage ||
            magazine?.magazineLangauge ||
            magazine?.monthlyMagazineLanguage ||
            magazineLang;
        const langLabel =
            getLangLabel(rawLang) ||
            getLangLabel(magazine?.monthlyMagazineLanguage) ||
            (isSimpleLangLabel(magazineLang) ? getLangLabel(magazineLang) : '') ||
            'hindi';
        const langForApi =
            magazine?.monthlyMagazineLanguageId ||
            magazineLanguageId ||
            (!isSimpleLangLabel(magazineLang) ? magazineLang : langLabel);

        logdetails.push(`lang=${langLabel}`);
        logdetails.push('navigate=/magazines/geetgovind');

        navigate('/magazines/geetgovind', {
            state: {
                productType: Product.MonthlyMagazine,
                geetGovindId: magazineId,
                magzineId: magazineId,
                geetGovindLang: langLabel,
                magazineLanguage: langForApi,
                bookName: getMagazineDisplayName(magazine),
                slug,
            }
        });

        console.log('[MagazinesCarousel]', logdetails.join(' | '));
    };

    const formattedTitles = useMemo(
        () => (magazinesList || []).map((magazine: any) =>
            formatMagazineTitle(getMagazineDisplayName(magazine), magazineProductType)
        ),
        [magazinesList, magazineProductType]
    );

    /** Distinguish tap from horizontal scroll on touch devices */
    const onItemPointerDown = (e: React.PointerEvent) => {
        itemTapRef.current = { x: e.clientX, y: e.clientY, moved: false };
    };

    const onItemPointerMove = (e: React.PointerEvent) => {
        const dx = Math.abs(e.clientX - itemTapRef.current.x);
        const dy = Math.abs(e.clientY - itemTapRef.current.y);
        if (dx > 8 || dy > 8) {
            itemTapRef.current.moved = true;
        }
    };

    const onItemActivate = (magazine: any, e: React.MouseEvent | React.PointerEvent) => {
        if (itemTapRef.current.moved) {
            itemTapRef.current.moved = false;
            return;
        }
        e.stopPropagation();
        handleMagazineClick(magazine);
    };

    /** Render formatted title lines for carousel item */
    const renderFormattedTitle = (formatted: FormattedMagazineTitle) => {
        if (formatted.kind === 'split') {
            return (
                <>
                    {formatted.line1}
                    <br />
                    {formatted.line2}
                </>
            );
        }

        return formatted.text;
    };

    return (
        <Box sx={embedded ? {} : {
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '12px',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', mb: 2 }}>
                <div
                    onClick={handleViewAllClick}
                    style={{
                        backgroundColor: "#f97316",
                        padding: '5px 10px',
                        height: 30,
                        borderRadius: 3,
                        color: "white",
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'chanakyaUni',
                        cursor: 'pointer',
                        fontSize: '14px',
                    }}
                >
                    {t('view_all_tr')} {totalRecords} {itemType || t('itemtype_books_tr')} {t('view_all_suffix_tr')}
                </div>
            </Box>
            <Box sx={{ position: 'relative' }}>
                <Box
                    ref={scrollRef}
                    onScroll={syncThumb}
                    sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        gap: '1px',
                        pb: showThumb ? `${HIT_THICKNESS + 6}px` : 1,
                        WebkitOverflowScrolling: 'touch',
                        touchAction: 'pan-x',
                        // Hide native scrollbar — custom orange pill matches TOC style
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': { display: 'none', height: 0 },
                    }}
                >
                    {(magazinesList || []).map((magazine: any, index: number) => (
                        <Box key={magazine?.id || index} sx={{
                            flexShrink: 0,
                            width: '120px',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer',
                            touchAction: 'manipulation',
                        }}
                            onPointerDown={onItemPointerDown}
                            onPointerMove={onItemPointerMove}
                            onClick={(e) => onItemActivate(magazine, e)}
                        >
                            <Box sx={{
                                width: '100px',
                                height: '150px',
                                overflow: 'hidden',
                                mb: 1,
                            }}>
                                <img
                                    src={
                                        magazine?.monthlyMagazineThumbPath
                                        || magazine?.kalyanThumbPath
                                        || magazine?.kalyanKalpataruThumbPath
                                        || "https://placehold.co/100x150/E0E0E0/333333?text=No+Image"
                                    }
                                    alt={getMagazineDisplayName(magazine) || "Magazine"}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'none' }}
                                    onError={(e: any) => { e.target.onerror = null; e.target.src = "https://placehold.co/100x150/E0E0E0/333333?text=No+Image"; }}
                                />
                            </Box>
                            <Typography variant="body2" sx={{
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                color: '#555',
                                fontFamily: 'chanakyaUni',
                                whiteSpace: 'normal',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                lineHeight: 1.25,
                            }}>
                                {renderFormattedTitle(formattedTitles[index] || formatMagazineTitle(getMagazineDisplayName(magazine), magazineProductType))}
                            </Typography>
                        </Box>
                    ))}
                    <Box sx={{
                        flexShrink: 0,
                        width: '120px',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer'
                    }}
                        onClick={handleViewAllClick}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px',
                                textAlign: 'center',
                            }}
                        >
                            <Avatar sx={{ bgcolor: '#f97316', width: 35, height: 35 }}>
                                <ArrowForwardIosIcon sx={{ fontSize: 16 }} onClick={handleViewAllClick} />
                            </Avatar>
                            <Typography className='mt-2' style={{ fontSize: '14px', color: "#f97316" }}>{t('view_all_tr')}</Typography>
                        </div>
                    </Box>
                </Box>
                {showThumb && (
                    <Box
                        ref={trackRef}
                        onPointerDown={onTrackPointerDown}
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: `${HIT_THICKNESS}px`,
                            zIndex: 2,
                            pointerEvents: 'none',
                            touchAction: 'none',
                            cursor: 'pointer',
                            background: `linear-gradient(to bottom, transparent ${(HIT_THICKNESS - TRACK_THICKNESS) / 2}px, rgba(71, 45, 30, 0.12) ${(HIT_THICKNESS - TRACK_THICKNESS) / 2}px, rgba(71, 45, 30, 0.12) ${(HIT_THICKNESS - TRACK_THICKNESS) / 2 + TRACK_THICKNESS}px, transparent ${(HIT_THICKNESS - TRACK_THICKNESS) / 2 + TRACK_THICKNESS}px)`,
                        }}
                    >
                        <Box
                            data-thumb="1"
                            onPointerDown={onThumbPointerDown}
                            onPointerMove={onThumbPointerMove}
                            onPointerUp={onThumbPointerUp}
                            onPointerCancel={onThumbPointerUp}
                            sx={{
                                position: 'absolute',
                                left: `${thumbLeft}px`,
                                top: `${(HIT_THICKNESS - PILL_THICKNESS) / 2}px`,
                                width: `${THUMB_SIZE}px`,
                                height: `${PILL_THICKNESS}px`,
                                borderRadius: '999px',
                                backgroundColor: THUMB_COLOR,
                                cursor: 'grab',
                                touchAction: 'none',
                                pointerEvents: 'auto',
                                '&:active': { cursor: 'grabbing' },
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};
export default MagazinesCarousel
